/**
 * Link Coordination Model Tool
 *
 * Links coordination models (.nwd, .nwc) into the project for
 * clash review, mirroring the BIM Insert > Link panel entry. Binary
 * These containers cannot be decoded in this build, so the reference is
 * registered in the shared LinkManager (visible in Manage Links) and a
 * placeholder footprint element marks the model's position in the scene.
 */

import { v4 as uuidv4 } from 'uuid';
import { LinkElement, LinkGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import i18n from '../i18n';

export class LinkCoordinationModelTool extends BaseTool {
    id = 'link_coordination_model';
    name = 'Link Coordination Model';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    public executeLink(filePath: string, size = 0) {
        const link = this.linkManager.linkFile('Coordination', filePath, {
            positioning: 'OriginToOrigin',
            size
        });
        const element = this.createLinkElement(link);
        this.linkManager.setDocumentId(link.id, element.id);
        return link;
    }

    onActivate(): void {
        // Dialog-driven command: pick a coordination model.
        void this.pickFile('.nwd,.nwc,.rvt,.ifc').then((file) => {
            if (file) {
                this.executeLink(file.name, file.size);
                this.notify(i18n.t('tools.notify.coordModelLinked', { fileName: file.name }), 'success');
            }
            this.exitToSelect();
        });
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: nothing to place interactively.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createLinkElement(link: any) {
        const geometryData: LinkGeometryData = {
            linkId: link.id,
            origin: [0, 0, 0],
            scale: [1, 1, 1],
            rotation: [0, 0, 0],
            boxSize: [60, 60, 24]
        };

        const linkElement: LinkElement = {
            id: uuidv4(),
            category: 'Other Links',
            type: 'Coordination Model: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Status': link.status,
                'Role': 'Coordination'
            },
            constraints: [],
            geometryData
        };

        this.context?.bimStore.addElement(linkElement);
        return linkElement;
    }
}
